import React, { useState } from 'react';
import { Input } from './ui/input';

/**
 * Componente de input para DNI/RUT con formateo automático
 * Soporta:
 * - RUT chileno: XX.XXX.XXX-X (con dígito verificador)
 * - DNI argentino: XX.XXX.XXX (sin dígito verificador)
 */
const DniRutInput = React.forwardRef(({ value, onChange, onValidChange, ...props }, ref) => {
  const [error, setError] = useState('');
  const [tipo, setTipo] = useState(''); // 'CL' o 'AR'

  // Limpiar: remover todo excepto números y K
  const cleanValue = (val) => {
    return val.replace(/[^0-9kK]/g, '').toUpperCase();
  };

  // Formatear RUT chileno: XX.XXX.XXX-X
  const formatRutChileno = (cleaned) => {
    if (cleaned.length === 0) return '';
    
    const dv = cleaned.slice(-1);
    const numero = cleaned.slice(0, -1);
    
    if (numero.length === 0) return dv;
    
    let formatted = '';
    for (let i = numero.length - 1, count = 0; i >= 0; i--, count++) {
      if (count > 0 && count % 3 === 0) {
        formatted = '.' + formatted;
      }
      formatted = numero[i] + formatted;
    }
    
    return `${formatted}-${dv}`;
  };

  // Formatear DNI argentino: XX.XXX.XXX
  const formatDniArgentino = (cleaned) => {
    if (cleaned.length === 0) return '';
    
    let formatted = '';
    for (let i = cleaned.length - 1, count = 0; i >= 0; i--, count++) {
      if (count > 0 && count % 3 === 0) {
        formatted = '.' + formatted;
      }
      formatted = cleaned[i] + formatted;
    }
    
    return formatted;
  };

  // Validar RUT chileno
  const validarRutChileno = (cleaned) => {
    if (cleaned.length < 2) return false;
    
    const numero = cleaned.slice(0, -1);
    const dv = cleaned.slice(-1);
    
    if (!/^\d+$/.test(numero)) return false;
    
    let suma = 0;
    let multiplicador = 2;
    
    for (let i = numero.length - 1; i >= 0; i--) {
      suma += parseInt(numero[i]) * multiplicador;
      multiplicador = multiplicador < 7 ? multiplicador + 1 : 2;
    }
    
    const resto = suma % 11;
    const dvCalculado = 11 - resto;
    
    let dvEsperado;
    if (dvCalculado === 11) dvEsperado = '0';
    else if (dvCalculado === 10) dvEsperado = 'K';
    else dvEsperado = String(dvCalculado);
    
    return dv === dvEsperado;
  };

  // Validar DNI argentino (solo verificar longitud)
  const validarDniArgentino = (cleaned) => {
    return /^\d{7,8}$/.test(cleaned);
  };

  // Detectar tipo y validar
  const detectarYValidar = (cleaned) => {
    // Si tiene K, es RUT chileno
    if (cleaned.includes('K')) {
      setTipo('CL');
      if (cleaned.length >= 8 && cleaned.length <= 9) {
        if (validarRutChileno(cleaned)) {
          setError('');
          return { valid: true, tipo: 'CL' };
        } else {
          setError('RUT chileno inválido');
          return { valid: false, tipo: 'CL' };
        }
      }
      return { valid: false, tipo: 'CL' };
    }
    
    // Si tiene 8-9 dígitos, puede ser RUT chileno o DNI argentino
    if (cleaned.length >= 7 && cleaned.length <= 9) {
      // Primero intentar validar como RUT chileno
      if (cleaned.length >= 8 && validarRutChileno(cleaned)) {
        setTipo('CL');
        setError('');
        return { valid: true, tipo: 'CL' };
      }
      
      // Si no, validar como DNI argentino
      if (validarDniArgentino(cleaned)) {
        setTipo('AR');
        setError('');
        return { valid: true, tipo: 'AR' };
      }
    }
    
    if (cleaned.length > 0 && cleaned.length < 7) {
      setError('');
      setTipo('');
    } else if (cleaned.length >= 7) {
      setError('DNI/RUT inválido');
    }
    
    return { valid: false, tipo: '' };
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    const cleaned = cleanValue(inputValue);
    
    // Detectar tipo y validar
    const validation = detectarYValidar(cleaned);
    
    // Formatear según el tipo detectado o la longitud
    let formatted;
    if (cleaned.includes('K') || (cleaned.length >= 8 && cleaned.length <= 9)) {
      // Probable RUT chileno
      formatted = formatRutChileno(cleaned);
    } else if (cleaned.length >= 7 && cleaned.length <= 8 && /^\d+$/.test(cleaned)) {
      // Probable DNI argentino
      formatted = formatDniArgentino(cleaned);
    } else {
      // Formato neutral mientras escribe
      formatted = formatDniArgentino(cleaned);
    }
    
    if (onChange) {
      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: formatted }
      };
      onChange(syntheticEvent);
    }
    
    if (onValidChange) {
      onValidChange(validation.valid, cleaned, validation.tipo);
    }
  };

  return (
    <div className="space-y-1">
      <Input
        ref={ref}
        {...props}
        value={value}
        onChange={handleChange}
        placeholder="Ej: 12.345.678-9 (CL) o 12.345.678 (AR)"
        className={error ? 'border-red-500' : props.className}
      />
      <div className="flex justify-between text-xs">
        {error && <span className="text-red-600">{error}</span>}
        {tipo && !error && (
          <span className="text-green-600 ml-auto">
            {tipo === 'CL' ? '✓ RUT Chileno' : '✓ DNI Argentino'}
          </span>
        )}
      </div>
    </div>
  );
});

DniRutInput.displayName = 'DniRutInput';

export default DniRutInput;
